import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Link2, ArrowRight, Loader2, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { getMyOrganizations } from '../services/organizationService';
import api from '../lib/axios';
import { queryClient } from '../lib/queryClient';
import Wordmark from '../components/Brand';
import type { ApiResponse } from '@maios/shared';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [loading, setLoading] = useState(true);
  const [showInviteInput, setShowInviteInput] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [joining, setJoining] = useState(false);

  // 이미 조직이 있으면 대시보드로 리다이렉트
  useEffect(() => {
    getMyOrganizations()
      .then((orgs) => {
        if (orgs.length > 0) {
          navigate('/', { replace: true });
        }
      })
      .catch(() => {
        // 조직 조회 실패 시 온보딩 표시
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleJoinWithCode = async () => {
    const code = inviteCode.trim();
    if (!code) {
      toast.error('초대 코드를 입력해주세요.');
      return;
    }

    setJoining(true);
    try {
      await api.post<ApiResponse>(`/invitations/${code}/accept`);
      toast.success('조직에 합류했습니다!');
      navigate('/', { replace: true });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data
          ?.message ??
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        '초대 코드가 유효하지 않습니다.';
      toast.error(msg);
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="size-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <Wordmark className="text-3xl mb-2" />
          <h2 className="text-2xl font-semibold text-gray-900">
            {user?.name ? `${user.name}님, ` : ''}환영합니다!
          </h2>
          <p className="mt-2 text-gray-500">시작하려면 아래 옵션 중 하나를 선택하세요.</p>
        </div>

        {/* Cards */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Card A: 새 워크스페이스 만들기 */}
            <button
              onClick={() => navigate('/onboarding/create')}
              className="group border-2 border-gray-200 hover:border-primary-500 rounded-xl p-6
                cursor-pointer transition-all text-left hover:shadow-md"
            >
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4
                group-hover:bg-primary-200 transition-colors">
                <Building2 className="size-6 text-primary-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                새 워크스페이스 만들기
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                새로운 팀을 위한 워크스페이스를 생성합니다.
              </p>
              <span className="inline-flex items-center gap-1 text-sm text-primary-600 font-medium
                group-hover:gap-2 transition-all">
                시작하기 <ArrowRight className="size-4" />
              </span>
            </button>

            {/* Card B: 초대 코드로 합류 */}
            <button
              onClick={() => setShowInviteInput(true)}
              className={`group border-2 rounded-xl p-6 cursor-pointer transition-all text-left hover:shadow-md
                ${showInviteInput ? 'border-primary-500 shadow-md' : 'border-gray-200 hover:border-primary-500'}`}
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4
                group-hover:bg-blue-200 transition-colors">
                <Link2 className="size-6 text-blue-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                초대 코드로 합류하기
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                이미 초대를 받으셨나요? 초대 코드를 입력하세요.
              </p>
              {!showInviteInput && (
                <span className="inline-flex items-center gap-1 text-sm text-blue-600 font-medium
                  group-hover:gap-2 transition-all">
                  코드 입력 <ArrowRight className="size-4" />
                </span>
              )}
            </button>
          </div>

          {/* 초대 코드 입력 영역 */}
          {showInviteInput && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <label htmlFor="invite-code" className="block text-sm font-medium text-gray-700 mb-2">
                초대 코드
              </label>
              <div className="flex gap-2">
                <input
                  id="invite-code"
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleJoinWithCode()}
                  placeholder="초대 코드를 입력하세요"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm
                    focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  autoFocus
                />
                <button
                  onClick={handleJoinWithCode}
                  disabled={joining || !inviteCode.trim()}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium
                    hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors
                    whitespace-nowrap"
                >
                  {joining ? '합류 중...' : '합류하기'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 로그아웃 */}
        <div className="text-center mt-6">
          <button
            onClick={() => { logout(); queryClient.clear(); navigate('/login', { replace: true }); }}
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            <LogOut className="size-4" />
            다른 계정으로 로그인
          </button>
        </div>
      </div>
    </div>
  );
}
